import { useState, forwardRef, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  Slide,
  Autocomplete,
  createFilterOptions,
  FormHelperText,
  IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import jsPDF from 'jspdf';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  CapitializeText,
  extractHierarchyInfoFromPath,
  getAdminRoleFromHierarchy,
  parseValidationErrorMessage,
  parseValidationErrorMessageForBulkUpload,
  removeNulls
} from '../../../utils/Utilities';
import { useAuth } from '../../customContextProviders/AuthContext';
import { AlertDialog, SnackBarAlert } from '../../HelperComponents';
import {
  MerchantBulkUpload,
  AddNewMerchant,
  UpdateMerchant,
  GetAllMerchants
} from '../../../network/Merchants';
import {
  APP_CONSTANTS,
  DEVICE_KEYS_CBI,
  DEVICE_TYPES_CBI,
  indianStates
} from '../../../URLConstants';
import mccData from '../../../../public/static/mcc.json';
import { ConfirmDialog } from '../app';

// ----------------------------------------------------------------------

/* eslint-disable */
const SlideTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
/* eslint-enable */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

const style = {
  position: 'absolute',
  width: '100%',
  maxWidth: { sm: '90%', xs: '95%', md: 600 },
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '70%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: 1
};

const DeviceSchemaCBI = Yup.object().shape({
  merchantAddress: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    )
    .matches(/^[\p{L}\p{N}\s]+$/u, 'Special characters are not allowed'),

  merchantCity: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    ),

  merchantMobileNo: Yup.string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Invalid phone number, Must be 10 digits.'),

  merchantName: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    )
    .test('no-double-space', 'Double spaces are not allowed', (value) => !value || !/\s{2}/.test(value))
    .matches(/^[\p{L}\p{N}\s]+$/u, 'Special characters are not allowed'),

  merchantPincode: Yup.string()
    .required('Required')
    .matches(/^[0-9]{6}$/, 'Pincode must be exactly 6 digits'),

  merchantState: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    ),

  deviceLanguage: Yup.string().required('Required'),

  merchantBranchId: Yup.string().required('Required'),

  uniqueRef: Yup.string()
    .trim()
    .matches(/^\d{10}$/, 'Must be 10 digits.')
    .required('Required'),

  mcc: Yup.string()
    .required('MCC is required')
    .matches(/^\d{3,4}$/, 'MCC must be 3 or 4 digits'),
  accountType: Yup.string().required('Required'),

  isVerified: Yup.string().required('Required'),

  merchantGenre: Yup.string().required('Required'),
  // qrType: Yup.string().required('Required'),
  paymentInterfaceType: Yup.string().required('Required')
});

NewNoxDeviceRequestFormModal.propTypes = {
  merchantData: PropTypes.object,
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  _onAction: PropTypes.func,
  accountDetails: PropTypes.object,
  branchesList: PropTypes.object
};

export default function NewNoxDeviceRequestFormModal({
  merchantData,
  opneAssignedModal,
  handleClose,
  _onAction,
  accountDetails,
  branchesList,
  fetchBranchesList,
  regionsList,
  languageList
}) {
  const { sessionUserData } = useAuth();
  const fileInputRef = useRef(null);

  const isMakerOrChecker =
    sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_MAKER ||
    sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_CHECKER;

  const adminRole = getAdminRoleFromHierarchy(accountDetails?.path);
  const isHOAdmin = adminRole === 'HOAdmin';

  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [alertDialogMetas, setAlertDialogMetas] = useState({
    message: '',
    title: '',
    btnLabel: '',
    bulkUploadError: null
  });

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    loading: false,
    message: '',
    onConfirm: () => {},
    onCancel: () => setOpenConfirm(false),
    confirmText: ''
  });

  const [error, setError] = useState({
    accountNo: false,
    phone: false
  });

  const addNoxDeviceFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      merchantAddress: merchantData?.address || '',
      merchantCity: merchantData?.city || '',
      merchantId: '',
      merchantMobileNo: merchantData?.phone || '',
      merchantName: merchantData?.name || '',
      merchantPincode: merchantData?.pincode || '',
      merchantState: merchantData?.region || merchantData?.state || '',
      qrString: '',
      deviceLanguage: merchantData?.language?.toLowerCase() || '',
      merchantNbg: '',
      merchantBranch: '',
      merchantBranchId: merchantData?.branchId || '',
      merchantVpa: '',
      uniqueRef: merchantData?.details?.uniqueRef || '',
      mcc: Number(merchantData?.details?.mcc) || '',
      gstNumber: merchantData?.details?.gstIn || '',
      accountType: merchantData?.details?.accountType || '',
      isVerified: isHOAdmin ? merchantData?.details?.isVerified || '' : 'N',
      merchantGenre: 'OFFLINE',
      regionalOfficeName: '',
      regionalOfficeId: '',
      // qrType: merchantData?.details?.qrType || '',
      // qrType: 'STATIC',
      paymentInterfaceType: merchantData?.details?.paymentInterfaceType || ''
    },
    validationSchema: DeviceSchemaCBI,
    onSubmit: () => {
      const formatMcc = (mcc) => {
        if (!mcc) return mcc; // if it's null/undefined, just return it
        const mccStr = String(mcc);
        return mccStr.length === 3 ? `0${mccStr}` : mccStr;
      };

      const payload = removeNulls({
        name: addNoxDeviceFormik?.values?.merchantName,
        phone: addNoxDeviceFormik?.values?.merchantMobileNo,
        branch: CapitializeText(addNoxDeviceFormik?.values?.merchantCity),
        branchId: addNoxDeviceFormik?.values?.merchantBranchId.toUpperCase(),
        region: CapitializeText(addNoxDeviceFormik?.values?.merchantState),
        address: addNoxDeviceFormik?.values?.merchantAddress,
        state: addNoxDeviceFormik?.values?.merchantState,
        city: addNoxDeviceFormik?.values?.merchantCity,
        pincode: addNoxDeviceFormik?.values?.merchantPincode,
        image: 'https://example.com/image.jpg',
        language: addNoxDeviceFormik?.values?.deviceLanguage,
        // qrType: addNoxDeviceFormik?.values?.qrType,
        paymentInterfaceType: [addNoxDeviceFormik?.values?.paymentInterfaceType],
        details: {
          uniqueRef: addNoxDeviceFormik?.values?.uniqueRef,
          onBoardingType: 'BANK',
          isVerified: addNoxDeviceFormik?.values?.isVerified,
          mcc: formatMcc(addNoxDeviceFormik?.values?.mcc),
          gstIn: addNoxDeviceFormik?.values?.gstNumber,
          merchantGenre: addNoxDeviceFormik?.values?.merchantGenre,
          accountType: addNoxDeviceFormik?.values?.accountType
        }
      });
      const apiCall = () => {
        const call = merchantData
          ? UpdateMerchant(merchantData?.id, payload)
          : AddNewMerchant(payload);

        call
          .then((_res) => {
            addNoxDeviceFormik.setSubmitting(false);
            handleClose();
            setSnackBarMetas((prevState) => ({
              ...prevState,
              title: merchantData
                ? 'Merchant has been successfully updated'
                : 'Merchant has been successfully on-boarded',
              severity: 'success'
            }));
            setIsSnackBarAlertOpen(true);
            addNoxDeviceFormik.resetForm();
          })
          .catch((error) => {
            addNoxDeviceFormik.setSubmitting(false);
            setAlertDialogMetas({
              ...alertDialogMetas,
              message: parseValidationErrorMessage(error),
              title: 'Failed to process your request',
              btnLabel: 'OK'
            });
            setOpenAlertDialog(true);
          });
      };
      if (error?.accountNo || error?.phone) {
        let message = 'Merchant already exists with ';

        if (error?.accountNo && error?.phone) {
          message += `account number and phone number. Do you still want to proceed?`;
        } else if (error?.accountNo) {
          message += `account number. Do you still want to proceed?`;
        } else if (error?.phone) {
          message += `phone number. Do you still want to proceed?`;
        }

        setDialogConfig({
          loading: false,
          message,
          onConfirm: () => {
            setOpenConfirm(false);
            apiCall();
          },
          onCancel: () => {
            setOpenConfirm(false);
            addNoxDeviceFormik?.setSubmitting(false);
          },
          confirmText: 'Yes',
          cancelText: 'No'
        });

        setOpenConfirm(true);
      } else {
        apiCall();
      }
    }
  });

  useEffect(() => {
    if (accountDetails?.path && opneAssignedModal) {
      const regionalOfficeId = regionsList.find(
        (region) =>
          region.name === extractHierarchyInfoFromPath(accountDetails?.path)?.regionalOffice
      )?.id;
      fetchBranchesList(regionalOfficeId);
    }
    if (isMakerOrChecker && !merchantData) {
      setFieldValue(
        'merchantBranchId',
        extractHierarchyInfoFromPath(accountDetails?.path)?.branchId
      );
      setFieldValue(
        'regionalOfficeName',
        extractHierarchyInfoFromPath(accountDetails?.path)?.regionalOffice
      );
    }
  }, [accountDetails]);

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleBlur,
    handleChange,
    getFieldProps,
    resetForm,
    setFieldValue
  } = addNoxDeviceFormik;

  const handleFileUpload = (event) => {
    addNoxDeviceFormik.setSubmitting(true);
    MerchantBulkUpload(event.target.files[0])
      .then((_res) => {
        addNoxDeviceFormik.setSubmitting(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: 'Merchants have been successfully created !',
          severity: 'success'
        }));
        setIsSnackBarAlertOpen(true);
        handleClose();
      })
      .catch((error) => {
        addNoxDeviceFormik.setSubmitting(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setAlertDialogMetas({
          ...alertDialogMetas,
          message: parseValidationErrorMessageForBulkUpload(error),
          title: 'Error processing the file',
          btnLabel: 'OK',
          bulkUploadError: parseValidationErrorMessageForBulkUpload(error)
        });
        setOpenAlertDialog(true);
      });
  };

  function downloadBulkUploadSampleFile() {
    const link = document.createElement('a');
    link.href = process.env?.REACT_APP_B2B_CLIENT_BULK_UPLOAD_SAMPLE_FILE;
    link.download = 'merchant_onboarding_file.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function exportErrorReport() {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    const logoUrl = 'https://i.postimg.cc/FKLr13w7/logo.png';
    const img = new Image();
    img.src = logoUrl;
    img.onload = function () {
      const now = new Date();
      const formattedDate = now.toLocaleDateString();
      const formattedTime = now.toLocaleTimeString();

      doc.setFont('Helvetica', 'bold');
      doc.text(10, 11, `Error Report`);

      doc.setFont('Mulish', 'normal');
      doc.text(10, 20, `${formattedDate} ${formattedTime}`);

      const errorMessage =
        typeof alertDialogMetas?.bulkUploadError === 'string'
          ? alertDialogMetas?.bulkUploadError
          : JSON.stringify(alertDialogMetas?.bulkUploadError, null, 2);

      const maxWidth = 190; // Adjust this value as needed
      const textX = 10;
      const textY = 30;

      doc.text(errorMessage || 'NA', textX, textY, { maxWidth });

      doc.save(`error_report_${formattedDate}_${formattedTime}.pdf`);
      setOpenAlertDialog(false);
    };
  }

  const filter = createFilterOptions();

  const handlePhoneChange = async (e) => {
    const { value } = e.target;
    addNoxDeviceFormik.setFieldValue('merchantMobileNo', value);

    setError((prev) => ({
      ...prev,
      phone: false
    }));

    if (value.length === 10) {
      try {
        const res = await GetAllMerchants(1, 1, { phone: value });

        if (res?.data?.data && res.data.data.length > 0) {
          setError((prev) => ({
            ...prev,
            phone: true
          }));
        }
      } catch (err) {
        console.error('API Error', err);
      }
    }
  };

  const handleAccountNoChange = async (e) => {
    const { value } = e.target;
    addNoxDeviceFormik.setFieldValue('uniqueRef', value);

    setError((prev) => ({
      ...prev,
      accountNo: false
    }));

    if (value.length === 10) {
      try {
        const res = await GetAllMerchants(1, 1, { 'details.uniqueRef': value });

        if (res?.data?.data && res.data.data.length > 0) {
          setError((prev) => ({
            ...prev,
            accountNo: true
          }));
        }
      } catch (err) {
        console.error('API Error', err);
      }
    }
  };

  const handlePaymentInterfaceChange = (e) => {
    const next = e.target.value;

    if (next === DEVICE_KEYS_CBI.DYNAMIC) {
      setDialogConfig({
        loading: false,
        message: 'Monthly rental for dynamic soundbox is Rs 250. Kindly confirm to proceed.',
        onConfirm: () => {
          setOpenConfirm(false);
          setFieldValue('paymentInterfaceType', DEVICE_KEYS_CBI.DYNAMIC);
        },
        onCancel: () => {
          setOpenConfirm(false);
        },
        confirmText: 'Confirm',
        cancelText: 'Cancel'
      });

      setOpenConfirm(true);
      return;
    }

    setFieldValue('paymentInterfaceType', next);
  };

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        // onClose={() => {
        //   handleClose();
        //   addNoxDeviceFormik?.resetForm();
        // }}
        components={{ Transition: SlideTransition }}
        aria-labelledby="device-request-modal-title"
        aria-describedby="device-request-modal-description"
      >
        <FormikProvider value={addNoxDeviceFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box
              sx={{
                ...style,
                maxHeight: '90vh', // 90% of the viewport height
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                  New Assign Request
                </Typography>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat', color: '#f8fafb' }}
                >
                  CENTRAL BANK OF INDIA
                </Typography>
                <IconButton
                  data-testid="merchant-request-modal-close"
                  aria-label="Close merchant request form"
                  onClick={() => {
                    handleClose();
                    addNoxDeviceFormik?.resetForm();
                  }}
                  // sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {isMakerOrChecker ? (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth>
                      <TextField
                        {...getFieldProps('merchantBranchId')}
                        error={Boolean(touched.merchantBranchId && errors.merchantBranchId)}
                        helperText={touched.merchantBranchId && errors.merchantBranchId}
                        label="Branch Id *"
                        disabled={isMakerOrChecker}
                        InputLabelProps={{ shrink: isMakerOrChecker }}
                      />
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Autocomplete
                      options={branchesList || []}
                      getOptionLabel={(option) => option.branchId || ''}
                      value={
                        branchesList?.find((b) => b.branchId === values.merchantBranchId) || null
                      }
                      onChange={(e, newValue) => {
                        setFieldValue('merchantBranchId', newValue ? newValue.branchId : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Branch Id *"
                          error={Boolean(touched.merchantBranchId && errors.merchantBranchId)}
                          helperText={touched.merchantBranchId && errors.merchantBranchId}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Branch Id *'
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.branchId === value?.branchId}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantCity')}
                      error={Boolean(touched.merchantCity && errors.merchantCity)}
                      helperText={touched.merchantCity && errors.merchantCity}
                      label="Merchant City *"
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Autocomplete
                    freeSolo
                    options={mccData}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      if (option.inputValue) return option.inputValue;
                      return `${option.MCCName} (${option.MCCCode})`;
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      const isExisting = options.some((opt) => opt.MCCCode === inputValue);

                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          MCCName: `Add "${inputValue}"`,
                          MCCCode: inputValue
                        });
                      }

                      return filtered;
                    }}
                    value={mccData.find((opt) => opt.MCCCode === values.mcc) || values.mcc || null}
                    onChange={(event, newValue) => {
                      // Resolve the raw code string from whatever shape newValue comes in as
                      let code = '';
                      if (typeof newValue === 'string') code = newValue.trim();
                      else if (newValue?.inputValue) code = newValue.inputValue.trim();
                      else code = newValue?.MCCCode ?? '';

                      // Must be exactly 4 digits — 0999, 9999, 0180 all valid
                      if (/^\d{3,4}$/.test(code)) {
                        setFieldValue('mcc', code);
                      }
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof value === 'string') return option.MCCCode === value;
                      return option.MCCCode === value?.MCCCode;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Merchant Category Code *"
                        error={Boolean(touched.mcc && errors.mcc)}
                        helperText={touched.mcc && errors.mcc}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Merchant Category Code *'
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('uniqueRef')}
                      error={Boolean(touched.uniqueRef && errors.uniqueRef)}
                      helperText={touched.uniqueRef && errors.uniqueRef}
                      label="Account No *"
                      onChange={handleAccountNoChange}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField {...getFieldProps('gstNumber')} label="GST Number" />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="account-type-label">Account Type *</InputLabel>
                    <Select
                      labelId="account-type-label"
                      id="account-type-select"
                      label="Account Type"
                      {...getFieldProps('accountType')}
                      error={Boolean(touched.accountType && errors.accountType)}
                      helperText={touched.accountType && errors.accountType}
                    >
                      <MenuItem value="CURRENT" sx={{ p: 1.5 }}>
                        Current
                      </MenuItem>
                      <MenuItem value="SOD" sx={{ p: 1.5 }}>
                        SOD
                      </MenuItem>
                      <MenuItem value="SAVINGS" sx={{ p: 1.5 }}>
                        Savings
                      </MenuItem>

                      {/* 
                      <MenuItem value="OVERDRAFT" sx={{ p: 1.5 }}>
                        Overdraft
                      </MenuItem>                                             
                      <MenuItem value="CASHCREDIT" sx={{ p: 1.5 }}>
                        Cash Credit
                      </MenuItem><MenuItem value="SB" sx={{ p: 1.5 }}>
                            SB
                        </MenuItem>
                          <MenuItem value="OD" sx={{ p: 1.5 }}>
                            OD
                          </MenuItem>
                          <MenuItem value="UOD" sx={{ p: 1.5 }}>
                            UOD
                          </MenuItem>
                          <MenuItem value="CA" sx={{ p: 1.5 }}>
                            CA
                          </MenuItem> */}
                    </Select>
                    {touched.accountType && errors.accountType && (
                      <FormHelperText error>{errors.accountType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {/* <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="qr-language-request-label">QR Type *</InputLabel>
                    <Select
                      labelId="qr-language-request-label"
                      id="qr-language-request-select"
                      label="QR Type"
                      {...getFieldProps('qrType')}
                      error={Boolean(touched.qrType && errors.qrType)}
                      helperText={touched.qrType && errors.qrType}
                      disabled
                    >
                      <MenuItem value="STATIC" sx={{ p: 1.5 }}>
                        Static
                      </MenuItem>
                      <MenuItem value="DYNAMIC" sx={{ p: 1.5 }}>
                        Dynamic
                      </MenuItem>
                    </Select>
                    {touched.qrType && errors.qrType && (
                      <FormHelperText error>{errors.qrType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid> */}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="payment-interface-type-label">Soundbox Type *</InputLabel>
                    <Select
                      labelId="payment-interface-type-label"
                      id="payment-interface-type-select"
                      label="Soundbox Type *"
                      name="paymentInterfaceType"
                      value={
                        Array.isArray(values.paymentInterfaceType)
                          ? values.paymentInterfaceType[0] ?? ''
                          : values.paymentInterfaceType ?? ''
                      }
                      onChange={handlePaymentInterfaceChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.paymentInterfaceType && errors.paymentInterfaceType)}
                    >
                      {DEVICE_TYPES_CBI.map((item) => (
                        <MenuItem key={item.value} value={item.value} sx={{ p: 1.5 }}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>

                    {touched.paymentInterfaceType && errors.paymentInterfaceType && (
                      <FormHelperText error>{errors.paymentInterfaceType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="is-verified-label">Verified *</InputLabel>

                    <Select
                      labelId="is-verified-label"
                      id="is-verified-select"
                      label="Verified"
                      {...getFieldProps('isVerified')}
                      value={isHOAdmin ? values.isVerified : 'N'}
                      disabled={!isHOAdmin}
                      error={Boolean(touched.isVerified && errors.isVerified)}
                    >
                      <MenuItem value="Y" sx={{ p: 1.5 }}>
                        Yes
                      </MenuItem>

                      <MenuItem value="N" sx={{ p: 1.5 }}>
                        No
                      </MenuItem>
                    </Select>

                    <FormHelperText>
                      Verification required if turnover exceeds ₹50 Cr
                    </FormHelperText>
                    {touched.isVerified && errors.isVerified && (
                      <FormHelperText error>{errors.isVerified}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="merchant-genre-label">Merchant Genre *</InputLabel>
                    <Select
                      labelId="merchant-genre-label"
                      id="merchant-genre-select"
                      label="Merchant Genre"
                      {...getFieldProps('merchantGenre')}
                      error={Boolean(touched.merchantGenre && errors.merchantGenre)}
                      helperText={touched.merchantGenre && errors.merchantGenre}
                      disabled={isMakerOrChecker}
                    >
                      <MenuItem value="ONLINE" sx={{ p: 1.5 }}>
                        Online
                      </MenuItem>
                      <MenuItem value="OFFLINE" sx={{ p: 1.5 }}>
                        Offline
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantName')}
                      error={Boolean(touched.merchantName && errors.merchantName)}
                      helperText={touched.merchantName && errors.merchantName}
                      label="Merchant Name *"
                      inputProps={{ 'aria-label': 'Merchant Name *' }}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Autocomplete
                    freeSolo={false} // disables typing values not in the list
                    options={indianStates}
                    value={values?.merchantState}
                    onChange={(event, newValue) => setFieldValue('merchantState', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Merchant State *"
                        placeholder="Type to search..."
                        error={Boolean(touched.merchantState && errors.merchantState)}
                        helperText={touched.merchantState && errors.merchantState}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Merchant State *'
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="device-language-request-label">Language *</InputLabel>
                    <Select
                      labelId="device-language-request-label"
                      id="device-language-request-select"
                      label="Device Language"
                      {...getFieldProps('deviceLanguage')}
                      error={Boolean(touched.deviceLanguage && errors.deviceLanguage)}
                      helperText={touched.deviceLanguage && errors.deviceLanguage}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200
                          }
                        }
                      }}
                    >
                      {languageList?.data?.map((language) => (
                        <MenuItem key={language?.id} value={language?.shortCode} sx={{ p: 1.5 }}>
                          {language?.language}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.deviceLanguage && errors.deviceLanguage && (
                      <FormHelperText error>{errors.deviceLanguage}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantMobileNo')}
                      error={Boolean(touched.merchantMobileNo && errors.merchantMobileNo)}
                      helperText={touched.merchantMobileNo && errors.merchantMobileNo}
                      label="Merchant Mobile No *"
                      onChange={handlePhoneChange}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantPincode')}
                      error={Boolean(touched.merchantPincode && errors.merchantPincode)}
                      helperText={touched.merchantPincode && errors.merchantPincode}
                      label="Merchant Pincode *"
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantAddress')}
                      error={Boolean(touched.merchantAddress && errors.merchantAddress)}
                      helperText={touched.merchantAddress && errors.merchantAddress}
                      multiline
                      rows={2}
                      maxRows={4}
                      label="Merchant Address *"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Stack
                direction="row"
                justifyContent={merchantData ? 'flex-end' : 'space-between'}
                alignItems="center"
                spacing={1}
                sx={{ mt: 3, width: '100%' }}
              >
                {!merchantData &&
                sessionUserData?.user?.role !== APP_CONSTANTS?.USER_ROLE_CHECKER ? (
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={3}
                    sx={{ mt: 2 }}
                  >
                    <LoadingButton
                      loading={addNoxDeviceFormik?.isSubmitting}
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                    >
                      Bulk Upload
                      <VisuallyHiddenInput
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        type="file"
                        accept=".csv"
                      />
                    </LoadingButton>

                    <Box sx={{ display: 'flex' }}>
                      <LoadingButton
                        variant="text"
                        onClick={() => downloadBulkUploadSampleFile()}
                        sx={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: 'blue',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        Download bulk upload sample file{' '}
                      </LoadingButton>
                    </Box>

                    {/* <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'black' }}>Device in Production : </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'red', pl: 1 }}>---</Typography>
                  </Box>  */}
                  </Stack>
                ) : null}
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                  sx={{ mt: 3 }}
                >
                  {/* <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      handleClose();
                      addNoxDeviceFormik?.resetForm();
                    }}
                  >
                    Cancel
                  </Button> */}
                  <LoadingButton
                    loading={addNoxDeviceFormik.isSubmitting}
                    variant="contained"
                    color="info"
                    type="submit"
                  >
                    {merchantData ? 'Update' : 'Save'}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <AlertDialog
        openAlertDialog={openAlertDialog}
        message={alertDialogMetas?.message}
        title={alertDialogMetas?.title}
        btnLabel={alertDialogMetas?.btnLabel}
        handleClose={() => setOpenAlertDialog(false)}
        bulkUploadError={alertDialogMetas?.bulkUploadError}
        exportErrorReport={() => exportErrorReport()}
      />

      <ConfirmDialog
        open={openConfirm}
        message={dialogConfig.message}
        onCancel={dialogConfig.onCancel}
        onConfirm={dialogConfig.onConfirm}
        loading={dialogConfig?.loading}
        note={dialogConfig?.note}
        confirmText={dialogConfig?.confirmText}
        cancelText={dialogConfig?.cancelText}
      />
      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
